import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaPlus, FaMinus, FaTrash } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import AuthModal from "../../components/modals/AuthModal/AuthModal";
import "./RestaurantPage.css";
import config from "../../config";
import { getStateTaxRate } from "../../utils/taxRates";

const COMMISSION_RATE = 0.05;

// Robust function to load Google Maps script, inspired by BrowseKitchens
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve();
      return;
    }
    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      existingScript.onload = () => resolve();
      existingScript.onerror = (err) => reject(err);
      return;
    }
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.mapApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
};

/**
 * Parse a price string like "Individual $10 | Small $59 | Large $109" or "Individual $10 | $99".
 * Returns an object like { Individual: 10, Default: 99 } for incomplete labels.
 */
const parsePriceString = (priceString) => {
  const mapping = {};
  const parts = priceString.split("|");
  parts.forEach((part, index) => {
    const trimmed = part.trim();
    const match = trimmed.match(/(?:([^$]+)\s+)?\$(\d+(?:\.\d+)?)/);
    if (match) {
      const size = match[1]
        ? match[1].trim()
        : `Standard size ${index > 0 ? index : ""}`;
      const price = parseFloat(match[2]);
      mapping[size] = price;
    }
  });
  return mapping;
};

const getPrice = (item, size = null) => {
  if (typeof item.price === "string" && item.price.includes("|") && size) {
    const priceMapping = parsePriceString(item.price);
    return priceMapping[size] || 0;
  }
  return Number(item.price) || 0;
};

const getUniqueId = (item) => {
  if (item.foodID) return item.foodID;
  if (!item._uniqueClientId) {
    item._uniqueClientId = `${item.name}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  }
  return item._uniqueClientId;
};

function extractStateFromLocation(locationStr) {
  if (!locationStr) return "";
  const match = locationStr.match(/\b[A-Z]{2}\b/);
  return match ? match[0] : "";
}

const RestaurantPage = () => {
  const { name, restaurantID } = useParams();
  const decodedName = decodeURIComponent(name);
  const navigate = useNavigate();

  const [restaurant, setRestaurant] = useState(null);
  const [cart, setCart] = useState({});
  const [cartLoaded, setCartLoaded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoMessage, setPromoMessage] = useState("");
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryName, setDeliveryName] = useState("");
  const [deliveryPhone, setDeliveryPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(new Date());
  const [deliveryTime, setDeliveryTime] = useState(new Date());
  const [user, setUser] = useState(null);
  const [pendingSizeItem, setPendingSizeItem] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [tipPercentage, setTipPercentage] = useState(null);
  const [customTip, setCustomTip] = useState("");
  const [needCutlery, setNeedCutlery] = useState(false);
  const [numPeople, setNumPeople] = useState("");
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [scriptLoadError, setScriptLoadError] = useState(null);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const openAuthModal = (mode = "login") => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  // Add logging for isCartOpen changes
  const setIsCartOpenWithLog = (value) => {
    setIsCartOpen(value);
  };

  // Add logging for setCart
  const setCartWithLog = (value) => {
    setCart(value);
  };

  // Load user data from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      setDeliveryName(parsedUser.name || "");
      setDeliveryPhone(parsedUser.phone || "");
      setDeliveryAddress(parsedUser.address || "");
    }
  }, []);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCartWithLog(parsedCart);
        setCartLoaded(true);
      } catch (error) {
        console.error("Error parsing cart from localStorage:", error);
        setCartWithLog({});
        setCartLoaded(true);
      }
    } else {
      setCartLoaded(true);
    }
  }, []); // Run only once on mount

  // Fetch restaurant data
  useEffect(() => {
    const fetchRestaurantData = async () => {
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}/api/restaurants/${restaurantID}`
        );
        setRestaurant(response.data);
      } catch (error) {
        console.error(
          "Error fetching restaurant data:",
          error.response ? error.response.data : error.message
        );
      }
    };
    fetchRestaurantData();
  }, [restaurantID]);

  // Handle checkout status
  useEffect(() => {
    if (!cartLoaded || !restaurant) {
      return; // Wait until cart is loaded and restaurant is fetched
    }

    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    if (status === "success") {
      console.log("Clearing cart due to status=success");
      setCartWithLog({});
      localStorage.removeItem("cart");
      setIsCartOpenWithLog(false);
    } else if (
      status === "cancel" &&
      Object.keys(cart).length > 0 &&
      restaurant
    ) {
      console.log("Opening cart modal");
      setIsCartOpenWithLog(true);
    } else {
      console.log("Not opening cart modal. Conditions not met.");
    }
  }, [cartLoaded, restaurant]); // Remove cart from dependencies

  // Load Google Maps script once when component mounts
  useEffect(() => {
    loadGoogleMapsScript()
      .then(() => {
        setIsScriptLoaded(true);
        setScriptLoadError(null);
      })
      .catch((err) => {
        console.error("Error loading Google Maps script:", err);
        setScriptLoadError(
          "Failed to load Google Maps API. Autocomplete is unavailable."
        );
      });
  }, []);

  // Initialize autocomplete when modal opens and script is loaded
  useEffect(() => {
    if (!showDeliveryModal || !isScriptLoaded || !addressInputRef.current) {
      return;
    }

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error("Google Maps API is not available");
      return;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(
      addressInputRef.current,
      { types: ["address"], componentRestrictions: { country: "us" } }
    );
    autocomplete.setFields(["formatted_address", "address_components"]);
    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place && place.formatted_address) {
        setDeliveryAddress(place.formatted_address);
      }
    });
    autocompleteRef.current = { autocomplete, listener };

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.removeListener(
          autocompleteRef.current.listener
        );
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current.autocomplete
        );
        autocompleteRef.current = null;
      }
    };
  }, [showDeliveryModal, isScriptLoaded]);

  let foodItems = [];
  if (restaurant && restaurant.FoodItems) {
    if (Array.isArray(restaurant.FoodItems)) {
      foodItems = restaurant.FoodItems;
    } else if (restaurant.FoodItems.foodCategory) {
      const fc = restaurant.FoodItems.foodCategory;
      for (const cat in fc) {
        fc[cat].forEach((item) => {
          item.category = cat;
          foodItems.push(item);
        });
      }
    }
  }

  const categories = [
    "All",
    ...new Set(foodItems.map((fi) => fi.category || "Misc")),
  ];

  const filteredItems =
    selectedCategory === "All"
      ? foodItems
      : foodItems.filter((fi) => fi.category === selectedCategory);

  const getCartQuantity = (item) => {
    const uid = getUniqueId(item);
    return Object.keys(cart).reduce((acc, key) => {
      if (key === uid || key.startsWith(`${uid}|`)) {
        return acc + cart[key];
      }
      return acc;
    }, 0);
  };

  const handleAddToCart = (item, size = null) => {
    const uid = getUniqueId(item);
    const priceMapping =
      typeof item.price === "string" && item.price.includes("|")
        ? parsePriceString(item.price)
        : null;

    if (priceMapping && Object.keys(priceMapping).length > 1 && !size) {
      setPendingSizeItem(item);
      setSelectedSize("");
      return;
    }

    const selectedSize =
      size || (priceMapping ? Object.keys(priceMapping)[0] : null);
    const cartKey = selectedSize ? `${uid}|${selectedSize}` : uid;
    setCartWithLog((prev) => ({
      ...prev,
      [cartKey]: (prev[cartKey] || 0) + 1,
    }));
  };

  const handleRemoveFromCart = (item, size = null) => {
    const uid = getUniqueId(item);
    if (size) {
      const cartKey = `${uid}|${size}`;
      setCartWithLog((prev) => {
        const copy = { ...prev };
        if (copy[cartKey] > 1) {
          copy[cartKey] -= 1;
        } else {
          delete copy[cartKey];
        }
        return copy;
      });
      return;
    }

    const priceMapping =
      typeof item.price === "string" && item.price.includes("|")
        ? parsePriceString(item.price)
        : null;

    if (priceMapping && Object.keys(priceMapping).length > 1) {
      const matchingKeys = Object.keys(cart).filter(
        (k) => k === uid || k.startsWith(`${uid}|`)
      );
      if (matchingKeys.length === 1) {
        const cartKey = matchingKeys[0];
        setCartWithLog((prev) => {
          const copy = { ...prev };
          if (copy[cartKey] > 1) {
            copy[cartKey] -= 1;
          } else {
            delete copy[cartKey];
          }
          return copy;
        });
      } else if (matchingKeys.length > 1) {
        const sizesInCart = matchingKeys
          .map((mk) => mk.split("|")[1])
          .filter(Boolean);
        const chosenSize = window.prompt(
          `Which size of "${item.name}" to remove? Options: ${sizesInCart.join(
            ", "
          )}`
        );
        if (chosenSize && sizesInCart.includes(chosenSize)) {
          const cartKey = `${uid}|${chosenSize}`;
          setCartWithLog((prev) => {
            const copy = { ...prev };
            if (copy[cartKey] > 1) {
              copy[cartKey] -= 1;
            } else {
              delete copy[cartKey];
            }
            return copy;
          });
        }
      }
      return;
    }

    setCartWithLog((prev) => {
      const copy = { ...prev };
      if (copy[uid] > 1) {
        copy[uid] -= 1;
      } else {
        delete copy[uid];
      }
      return copy;
    });
  };

  const handleDeleteFromCart = (cartKey) => {
    setCartWithLog((prev) => {
      const copy = { ...prev };
      delete copy[cartKey];
      return copy;
    });
  };

  const handleConfirmSize = () => {
    if (!selectedSize) {
      alert("Please select a size first.");
      return;
    }
    const uid = getUniqueId(pendingSizeItem);
    const cartKey = `${uid}|${selectedSize}`;
    setCartWithLog((prev) => ({
      ...prev,
      [cartKey]: (prev[cartKey] || 0) + 1,
    }));
    setPendingSizeItem(null);
    setSelectedSize("");
  };

  const cartEntries = Object.entries(cart).map(([key, quantity]) => {
    let uid = key;
    let size = null;
    if (key.includes("|")) {
      [uid, size] = key.split("|");
    }
    const nameParts = uid.split("-");
    const itemName = nameParts.slice(0, -1).join("-");
    const item = foodItems.find((fi) => fi.name === itemName);
    return { key, item, quantity, size };
  });

  const subtotal = cartEntries.reduce((acc, ce) => {
    if (!ce.item) return acc;
    const price = ce.size ? getPrice(ce.item, ce.size) : getPrice(ce.item);
    return acc + price * ce.quantity;
  }, 0);

  const calculateTip = () => {
    if (tipPercentage) {
      return subtotal * (tipPercentage / 100);
    } else if (customTip) {
      return parseFloat(customTip) || 0;
    }
    return 0;
  };
  const tip = calculateTip();

  const commission = subtotal * COMMISSION_RATE;
  const deliveryCharge = 45;
  const stateAbbrev = extractStateFromLocation(restaurant?.location || "");
  const stateTaxRate = getStateTaxRate(stateAbbrev);
  const tax = subtotal * stateTaxRate;
  const taxAndCommission = tax + commission;
  let finalTotal = subtotal + taxAndCommission + deliveryCharge + tip;

  const handleApplyPromoCode = () => {
    if (promoCode.trim().toLowerCase() === "discount10") {
      setPromoMessage("Promo code applied! 10% off.");
      finalTotal = finalTotal * 0.9;
    } else {
      setPromoMessage("Promo code invalid or expired.");
    }
  };

  const handleCheckout = () => {
    if (subtotal < (restaurant?.min_order || 0)) {
      alert(
        `Minimum order is $${
          restaurant?.min_order
        }. You currently have $${subtotal.toFixed(
          2
        )}. Please add more items to proceed.`
      );
      return;
    }
    if (!user) {
      openAuthModal("login");
      return;
    }
    setShowDeliveryModal(true);
  };

  const validatePhoneNumber = (phone) => /^\+?\d{10,15}$/.test(phone);
  const validateAddress = (addr) => addr.trim().length >= 5;

  const handleConfirmDelivery = async () => {
    if (
      !deliveryName ||
      !deliveryPhone ||
      !deliveryAddress ||
      !deliveryDate ||
      !deliveryTime
    ) {
      alert("Please fill in all required fields.");
      return;
    }
    if (!validatePhoneNumber(deliveryPhone)) {
      alert("Please enter a valid phone number (10–15 digits, optionally +).");
      return;
    }
    if (!validateAddress(deliveryAddress)) {
      alert("Please enter a valid address (at least 5 characters).");
      return;
    }

    try {
      const checkoutData = {
        userID: user.userID,
        orderRestaurantUserID: restaurant.userID,
        totalAmount: finalTotal,
        cartItems: cartEntries
          .filter((ce) => ce.item)
          .map((ce) => ({
            id: getUniqueId(ce.item),
            name: ce.item.name,
            quantity: ce.quantity,
            price: ce.size ? getPrice(ce.item, ce.size) : getPrice(ce.item),
            ...(ce.size ? { size: ce.size } : {}),
          })),
        restaurantName: decodedName,
        restaurantID,
        tipAmount: tip,
        needCutlery,
        numPeople: numPeople || 0,
        taxAndCommission: taxAndCommission,
        deliveryDetails: {
          name: deliveryName,
          phone: deliveryPhone,
          address: deliveryAddress,
          date: deliveryDate.toISOString().split("T")[0],
          time: deliveryTime.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      };
      localStorage.setItem("cart", JSON.stringify(cart));
      const response = await axios.post(
        `${config.apiBaseUrl}/api/stripe/create-food-checkout-session`,
        checkoutData
      );
      window.location.href = response.data.url;
    } catch (err) {
      console.error("Error during checkout:", err);
    }
  };

  const totalCartQuantity = cartEntries.reduce(
    (sum, ce) => sum + ce.quantity,
    0
  );

  return (
    <div className="restaurant-page">
      <Navbar />
      {restaurant?.order_cutoff_time && (
        <div className="cutoff-banner">
          You must order at least 24 hours in advance for {restaurant.name}!
        </div>
      )}
      <header className="restaurant-header">
        {restaurant && (
          <motion.div
            className="header-content"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="header-image">
              <img
                src={
                  restaurant.images?.[0] ||
                  "https://via.placeholder.com/800x400"
                }
                alt={restaurant.name}
                className="restaurant-page-image"
              />
            </div>
            <div className="header-details">
              <h1>{restaurant.name}</h1>
              <p className="location">{restaurant.location}</p>
              <p className="min-order">
                Minimum Order: ${restaurant.min_order}
              </p>
              <div className="header-buttons">
                <button
                  className="view-menu-button"
                  onClick={() => window.open(restaurant.menu, "_blank")}
                >
                  View Menu
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </header>

      <div className="cart-float-button">
        <button
          className="cart-button"
          onClick={() => setIsCartOpenWithLog(true)}
        >
          Cart
          {totalCartQuantity > 0 && (
            <span className="cart-badge">{totalCartQuantity}</span>
          )}
        </button>
      </div>

      <main className="restaurant-content">
        <motion.nav
          className="category-bar"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {categories.map((cat) => (
            <motion.button
              key={cat}
              className={`category-item ${
                selectedCategory === cat ? "active" : ""
              }`}
              onClick={() => setSelectedCategory(cat)}
              whileHover={{
                scale: 1.1,
                backgroundColor: "#e63946",
                color: "#fff",
              }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {cat}
            </motion.button>
          ))}
        </motion.nav>

        <motion.section
          className="food-items-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <div className="food-items-grid">
            {filteredItems.map((item) => {
              const uid = getUniqueId(item);
              const priceMapping =
                typeof item.price === "string" && item.price.includes("|")
                  ? parsePriceString(item.price)
                  : null;
              const sizes = priceMapping ? Object.keys(priceMapping) : null;

              return (
                <motion.div
                  key={uid}
                  className="food-card"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 8px 15px rgba(0, 0, 0, 0.3)",
                  }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="food-card-content">
                    <h3>{item.name}</h3>
                    <p className="ingredients">{item.ingredients}</p>
                    {sizes ? (
                      <div className="price-sizes">
                        {sizes.map((size) => (
                          <p key={size} className="price">
                            {size === "Default" ? "Standard" : size}: $
                            {priceMapping[size].toFixed(2)}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="price">${getPrice(item).toFixed(2)}</p>
                    )}
                  </div>
                  <div className="quantity-controls">
                    <button onClick={() => handleRemoveFromCart(item)}>
                      <FaMinus />
                    </button>
                    <span>{getCartQuantity(item)}</span>
                    <button onClick={() => handleAddToCart(item)}>
                      <FaPlus />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.section>
      </main>

      <AnimatePresence>
        {isCartOpen && (
          <>
            <motion.div
              className="cart-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsCartOpenWithLog(false)}
            />
            <motion.div
              className="cart-modal"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3 }}
            >
              <div className="cart-modal-header">
                <h2>Your Cart</h2>
                <button
                  className="close-cart-button"
                  onClick={() => setIsCartOpenWithLog(false)}
                >
                  ×
                </button>
              </div>
              {!restaurant ? (
                <p>Loading food items...</p>
              ) : Object.keys(cart).length === 0 ? (
                <p className="empty-cart-msg">Your cart is empty</p>
              ) : (
                <>
                  <ul className="cart-items">
                    {cartEntries.map((ce) => {
                      if (!ce.item) return null;
                      const displaySize =
                        ce.size === "Default" ? "Standard" : ce.size;
                      return (
                        <li key={ce.key} className="cart-item">
                          <div className="item-info">
                            <span className="item-name">{ce.item.name}</span>
                            {ce.size && (
                              <span className="item-size">({displaySize})</span>
                            )}
                          </div>
                          <div className="cart-item-controls">
                            <button
                              className="icon-button"
                              onClick={() =>
                                handleRemoveFromCart(ce.item, ce.size)
                              }
                            >
                              <FaMinus />
                            </button>
                            <span>{ce.quantity}</span>
                            <button
                              className="icon-button"
                              onClick={() => handleAddToCart(ce.item, ce.size)}
                            >
                              <FaPlus />
                            </button>
                            <button
                              className="delete-button"
                              onClick={() => handleDeleteFromCart(ce.key)}
                            >
                              <FaTrash />
                            </button>
                          </div>
                          <span className="item-total">
                            $
                            {(
                              (ce.size
                                ? getPrice(ce.item, ce.size)
                                : getPrice(ce.item)) * ce.quantity
                            ).toFixed(2)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="promo-code-area">
                    <input
                      type="text"
                      placeholder="Enter promo code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                    />
                    <button
                      className="apply-promo-button"
                      onClick={handleApplyPromoCode}
                    >
                      Apply
                    </button>
                  </div>
                  {promoMessage && (
                    <p className="promo-message">{promoMessage}</p>
                  )}

                  <div className="cart-breakdown">
                    <div className="cart-row">
                      <span>Subtotal:</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="cart-row">
                      <span>Tax & Fees:</span>
                      <span>${taxAndCommission.toFixed(2)}</span>
                    </div>
                    <div className="cart-row">
                      <span>Delivery Fee:</span>
                      <span>${deliveryCharge.toFixed(2)}</span>
                    </div>
                    <div className="cart-row">
                      <span>Tip:</span>
                      <span>${tip.toFixed(2)}</span>
                    </div>
                    <div className="tip-section">
                      <div className="tip-options">
                        {[10, 17, 20].map((percent) => (
                          <motion.button
                            key={percent}
                            className={`tip-button ${
                              tipPercentage === percent ? "selected" : ""
                            }`}
                            onClick={() => {
                              setTipPercentage(percent);
                              setCustomTip("");
                            }}
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 5px 10px rgba(0,0,0,0.1)",
                            }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            {percent}%
                          </motion.button>
                        ))}
                        <div className="input-wrapper">
                          <span className="dollar-sign">$</span>
                          <motion.input
                            type="number"
                            placeholder="Other"
                            value={customTip}
                            onChange={(e) => {
                              setCustomTip(e.target.value);
                              setTipPercentage(null);
                            }}
                            className="custom-tip-input"
                            initial={{ opacity: 0.5 }}
                            whileFocus={{
                              opacity: 1,
                              borderColor: "#e63946",
                            }}
                            transition={{ duration: 0.2 }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="cart-row total">
                      <span>Total:</span>
                      <span>${finalTotal.toFixed(2)}</span>
                    </div>
                  </div>

                  <motion.button
                    className="checkout-button"
                    onClick={handleCheckout}
                    whileHover={{ scale: 1.05, backgroundColor: "#d62839" }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    Checkout
                  </motion.button>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {showDeliveryModal && (
        <div className="delivery-modal-backdrop">
          <div className="delivery-modal">
            <h2>Delivery Details</h2>
            {scriptLoadError && (
              <p
                className="error-message"
                style={{ color: "red", marginBottom: "10px" }}
              >
                {scriptLoadError}
              </p>
            )}
            <div className="form-group">
              <label>Contact:</label>
              <input
                type="text"
                placeholder="Enter your name"
                value={deliveryName}
                onChange={(e) => setDeliveryName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <input
                type="tel"
                placeholder="(555) 123-4567"
                value={deliveryPhone}
                onChange={(e) => setDeliveryPhone(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Address:</label>
              <input
                type="text"
                placeholder="Enter delivery address"
                ref={addressInputRef}
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Delivery Date:</label>
              <DatePicker
                selected={deliveryDate}
                onChange={(date) => setDeliveryDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="YYYY-MM-DD"
                className="datepicker-input"
              />
            </div>
            <div className="form-group">
              <DatePicker
                selected={deliveryTime}
                onChange={(time) => setDeliveryTime(time)}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="h:mm aa"
                placeholderText="HH:MM AM/PM"
                className="datepicker-input"
              />
            </div>
            <div className="form-group checkbox-inline">
              <label>
                <input
                  type="checkbox"
                  checked={needCutlery}
                  onChange={(e) => setNeedCutlery(e.target.checked)}
                />
                Need cutlery/plates?
              </label>
            </div>
            {needCutlery && (
              <div className="form-group">
                <label>Number of People:</label>
                <input
                  type="number"
                  placeholder="How many?"
                  value={numPeople}
                  onChange={(e) => setNumPeople(e.target.value)}
                />
              </div>
            )}
            <div className="delivery-modal-buttons">
              <button onClick={handleConfirmDelivery}>Confirm</button>
              <button onClick={() => setShowDeliveryModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingSizeItem && (
        <div className="size-select-backdrop">
          <div className="size-select-modal">
            <h3>Select size for {pendingSizeItem.name}</h3>
            <select
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
            >
              <option value="">Select size</option>
              {Object.entries(parsePriceString(pendingSizeItem.price)).map(
                ([size, price]) => (
                  <option key={size} value={size}>
                    {size === "Default" ? "Standard" : size} - $
                    {price.toFixed(2)}
                  </option>
                )
              )}
            </select>
            <div className="size-select-buttons">
              <button onClick={handleConfirmSize}>Confirm</button>
              <button
                onClick={() => {
                  setPendingSizeItem(null);
                  setSelectedSize("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <AuthModal
        open={showAuthModal}
        onClose={closeAuthModal}
        initialMode={authMode}
      />

      <Footer />
    </div>
  );
};

export default RestaurantPage;
